// A sheet as a place to work (docs/claude/sheets.md): the page's camera, which
// sheet is open, the selection of its objects, the paper tool armed, its paint
// (one layer list for screen and print, the viewports' cached bitmaps), its
// viewports' snapping, the pointer, wheel and keys on it, and printing or
// exporting it.
import { productName } from '../../shared/names';
import { matchesShortcut } from '../../shared/shortcuts';
import {
  type Bounds,
  type Cartiglio,
  CONNECTION_TYPES,
  type Discipline,
  DISCIPLINE_KINDS,
  type Lang,
  type ProductCategory,
  type SchematicImage,
  type Sheet,
  type SheetViewport,
  type TitleObject,
  type Vec2,
} from '../../shared/types';
import {
  beginGesture,
  changes,
  commitGesture,
  drawingRev,
  flashToolStatus,
  invalidate,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import { drawingDimStyle } from '../dimFormat';
import { updateDistEntry } from './distanceEntry';
import {
  doc,
  exportName,
} from './document';
import {
  drawSheetElecViews,
  elecAutoTip,
  elecDblClick,
  elecLayoutNow,
  elecMouseDown,
  elecMouseMove,
  elecMouseUp,
  elecSheet,
  elecViewAt,
  elecWireHover,
} from './elecSheet';
import {
  distToSegment,
  isFiniteBounds,
  marqueeTakes,
} from '../geometry';
import {
  drawGrips,
  GRIP_HIT_PX,
} from '../grip';
import { cancelUnderWay } from './underWayUi';
import {
  getUiLang,
  tr,
  trIn,
} from '../i18n';
import { curLang } from './nameFields';
import { numberedNames } from '../naming';
import { platform } from '../platform';
import {
  render,
  type RenderOptions,
} from '../render';
import {
  preloadSchematicImages,
  renderSchematic,
  schConnRoute,
  type SchemSel,
  schHitObject,
} from '../schematic';
import {
  allSheetObjects,
  schContent,
  sheetObjectPath,
  sheetObjects,
} from '../sheetObjects';
import {
  otherTableEdges,
  sheetScheduleTables,
} from './sheetPanel';
import {
  copySchematicSelection,
  deleteSchematicSelected,
  drawSchematicDrafts,
  schCaretOn,
  schDrag,
  schEditBoxId,
  schematicMouseDown,
  schematicMouseMove,
  schematicMouseUp,
  schFinishTextBox,
  schGetBox,
  schHandleAt,
  schOpenBoxEditor,
  schUid,
  schVertexAt,
  sheetSchematic,
} from './sheetSchematic';
import { renderViewTabs } from './sheetStrip';
import {
  FITTED_PAPER,
  fitViewportToBounds,
  type HandleId,
  panViewport,
  type PaperCamera,
  paperToScreen,
  type PaperView,
  paperViewOf,
  resizeViewport,
  scaleLabel,
  screenToPaper,
  sheetName,
  viewportAt,
  vpHandles,
  vpModelViewport,
  vpScreenRect,
  zoomPaperAbout,
} from '../sheets';
import {
  drawRevisionTable,
  drawScheduleTablePaper,
  drawTitleBlock,
  revisionTableHeightMm,
  scheduleTableHeightMm,
} from '../titleBlock';
import {
  commitTitleEdit,
  hitTitleObjHandle,
  openTitleObjInlineEditor,
  pushTitleObjHistory,
  resizeTitleObj,
  selectedTitleObj,
  selectedTitleObjs,
  selectTitleObjOnly,
  snapTitleObjMove,
  titleEditor,
  titleObjAt,
  titleObjHandles,
  titleObjSideDims,
  toggleTitleObjSelection,
} from './titleEditor';
import { setToolbarMode } from '../toolbar';
import {
  closeView3d,
  leavePlanTools,
  openView3d,
} from './view3d';
import {
  bgImagesForRender,
  canvas,
  cartiglioForSheet,
  cartiglioLogo,
  CATEGORY_DISCIPLINE,
  contentBounds,
  ctx,
  deselectAll,
  drainInstance,
  escapePaper,
  finishPaperWork,
  flashStatus,
  importDroppedFile,
  importedHidden,
  importedLayer,
  instanceImageMap,
  layers,
  loadImage,
  noteSpacePan,
  renderProductList,
  screenPos,
  setTool,
  SNAP_PX,
  spaceHeld,
  stenalLogoLoaded,
  templatesRevision,
  textEditor,
  textEditorUi,
  viewH,
  viewW,
} from '../main';

/** This module's state that main.ts reads and writes too. */
export const sheetSpace = {
  // The page's zoom / pan over its fit to the window (sheets.ts `PaperCamera`): reset
  // when switching sheets or on Zoom to Fit.
  paperCam: FITTED_PAPER as PaperCamera,
  /** Canvas-relative screen point of the last file drop, for schematic placement. */
  schDropScreen: null as Vec2 | null,
  sheetEnterId: null as string | null,
  sheetDrag: null as { mode: 'move' | 'resize' | 'pan' | 'paper'; handle?: HandleId; startScreen: Vec2; sx: number; sy: number; sw: number; sh: number; cx: number; cy: number } | null,
};

/** Where the active sheet's page is on screen NOW — worked out from the camera and
 *  the canvas, not kept from the last frame, so a hit test right after a zoom or a
 *  window resize reads the page where it is. */
export function paperView(): PaperView {
  const sh = activeSheet();
  return sh ? paperViewOf(sh, viewW(), viewH(), sheetSpace.paperCam) : { scale: 0.2, ox: 0, oy: 0 };
}
// Changes-table drag: move the whole table, or resize its width from either edge.
/** A table (`sheetTables`) being moved or widened: its rectangle, its height at a
 *  width, the gesture's start. */
let sheetTableDrag: { rect: { x: number; y: number; w: number; h: number }; heightAt: (w: number) => number; mode: 'move' | 'resize-w' | 'resize-w-left'; startScreen: Vec2; sx: number; sy: number; sw: number } | null = null;
// Dragging a placed schedule table (connection / preparation) on a sheet.

/** The page fitted to the canvas, with the user's zoom + pan applied. */
export function resetPaperView(): void { sheetSpace.paperCam = FITTED_PAPER; }

export function drawingSheets(): Sheet[] { return state.drawing.sheets ?? (state.drawing.sheets = []); }
export function activeSheet(): Sheet | null { return drawingSheets().find((s) => s.id === state.viewTab) ?? null; }
/** The viewport selected alone on the active sheet (a sheet object, `viewport`). */
export function selectedViewport(): SheetViewport | null {
  const sh = activeSheet();
  if (!sh || schemSel?.kind !== 'viewport' || schemSelSet.length !== 1) return null;
  const id = schemSel.id;
  return sh.viewports.find((v) => v.id === id) ?? null;
}
/** A viewport is locked (cannot be moved or deleted) unless explicitly unlocked;
 *  an absent `locked` field means locked, so viewports default to locked. */
export function viewportLocked(v: SheetViewport): boolean { return v.locked !== false; }
export function modelBoundsOrNull(): Bounds | null { const b = contentBounds(); return isFiniteBounds(b) ? b : null; }

/** Whether discipline `t` is shown in sheet `sh`'s viewports (default: shown). */
export function sheetDisciplineOn(sh: Sheet | undefined, t: Discipline): boolean {
  return sh?.disciplines?.[t] !== false;
}

function sheetRenderOpts(sh?: Sheet): RenderOptions {
  // Each sheet carries its own discipline visibility, defaulting to all shown.
  const filters = Object.fromEntries(DISCIPLINE_KINDS.map((d) => [d, sheetDisciplineOn(sh, d)])) as Record<Discipline, boolean>;
  // The schedule overlay (connection lines + tags) shows whenever any connection
  // discipline is on; flooring carries no connections, so it doesn't count here.
  const anyOn = CONNECTION_TYPES.some((d) => filters[d]);
  // Placed objects tagged with a disabled discipline are hidden in the viewport.
  const hidden = new Set((Object.keys(CATEGORY_DISCIPLINE) as ProductCategory[]).filter((c) => { const d = CATEGORY_DISCIPLINE[c]; return d != null && filters[d] === false; }));
  // The sheet's own hidden layers, on top of the ones hidden everywhere.
  const sheetHidden = new Set(sh?.hiddenLayers ?? []);
  return {
    layered: true,
    paint: layers().paint,
    revision: drawingRev(),
    hiddenLayers: sheetHidden,
    hideImported: importedHidden() || sheetHidden.has(importedLayer().id),
    underlay: true, // show imported PDF/DXF/image underlays inside each model viewport
    texts: state.drawing.texts ?? [], // text boxes are part of the model, so show them too
    showGrid: false,
    selectedId: null,
    selectedIds: [],
    selectedDimId: null,
    // Show connection lines (+ number tags) in viewports, filtered per discipline
    // by disciplineFilters below; the schedule tables stay in the model view only.
    showSchedule: anyOn,
    showScheduleTables: false,
    // Hatches and product-name labels are toggled per sheet (like the model
    // view's bottom-bar toggles), defaulting to hidden.
    showHatches: sh?.showHatches === true,
    showLabels: sh?.showLabels === true,
    showBeams: sh?.showBeams !== false,
    showCeilings: sh?.showCeilings === true,
    disciplineFilters: filters,
    backgroundImages: bgImagesForRender(),
    instanceImages: instanceImageMap(),
    lang: curLang(),
    hiddenCategories: hidden,
    // The drawing's dimension style is a property of the DRAWING, so a sheet's
    // viewports (and the 300-dpi print, which builds on these same options) label
    // their dimensions exactly as the model view does. Leaving it out reset every
    // viewport to 'auto' and silently ignored the chosen style on paper.
    dimStyle: drawingDimStyle(state.drawing),
    polygons: state.drawing.polygons ?? [],
    walls: state.drawing.walls ?? [], // user-drawn walls/lines are part of the model too
    // Flooring finishes are part of the model too; render.ts hides them when this
    // sheet's flooring discipline is toggled off (via `disciplineFilters.flooring`).
    floorings: (state.drawing.floorings ?? []).map((f) => ({
      floor: f, selected: false, drainInst: f.drain ? drainInstance(f.drain) : null, drainSel: false,
    })),
  } as RenderOptions;
}

/** Transient selection of a schematic object on the active schematic sheet.
 *  `schemSel` is the PRIMARY (last-picked) object — it drives handles, resize and
 *  the single-object panel; `schemSelSet` is the whole selection (shift/ctrl-click
 *  builds it) and always contains the primary as its last element. The two are
 *  kept in lock-step through `setSchemSel` / `toggleSchemSel`, so no site can
 *  leave a stale set behind. */
export let schemSel: SchemSel | null = null;
export let schemSelSet: SchemSel[] = [];
const schemSelKey = (s: SchemSel): string => `${s.kind}:${s.id}`;
/** Replace the whole selection with `hit` (or clear it). */
export function setSchemSel(hit: SchemSel | null): void {
  if (!hit || hit.kind !== 'elec' || schemSel?.kind !== 'elec' || schemSel.id !== hit.id) elecSheet.elecSub = null;
  schemSel = hit;
  schemSelSet = hit ? [hit] : [];
}
/** Shift/ctrl-click: toggle `hit` in or out of the multi-selection; the primary
 *  becomes the new last member (or null when the set empties). */
export function toggleSchemSel(hit: SchemSel): void {
  const k = schemSelKey(hit);
  const without = schemSelSet.filter((s) => schemSelKey(s) !== k);
  schemSelSet = without.length < schemSelSet.length ? without : [...schemSelSet, hit];
  schemSel = schemSelSet.length ? schemSelSet[schemSelSet.length - 1] : null;
  elecSheet.elecSub = null;
}
export const schemSelHas = (hit: SchemSel): boolean => schemSelSet.some((s) => schemSelKey(s) === schemSelKey(hit));
/** Exactly these sheet objects selected, the last the primary. */
export function setSchemSelSet(list: SchemSel[]): void {
  schemSelSet = list;
  schemSel = list[list.length - 1] ?? null;
  elecSheet.elecSub = null;
}
/** The sheet objects' adapters (sheetObjects.ts): an electrical view's frame is
 *  its layout's, worked out from the drawing. */
export const SHEET_OBJECTS = sheetObjects({ elecBounds: (v) => elecLayoutNow(v).bounds });
/** The sheet object a Select click would pick, outlined (paper mm) — the sheet
 *  twin of `canvasHover`. */
export let sheetHover: { key: string; ring: Vec2[]; closed: boolean } | null = null;

/** A sheet object's outline in paper mm: a box's / image's rectangle, a line's
 *  or a connection's path. */
function schObjectPath(sh: Sheet, sel: SchemSel): { ring: Vec2[]; closed: boolean } | null {
  return sheetObjectPath(sh, SHEET_OBJECTS, sel);
}

/** Select what a sheet marquee from `a` to `b` takes (paper mm; the same window /
 *  crossing rule as the model), replacing the selection or — `add` — adding. */
function applySheetMarquee(sh: Sheet, a: Vec2, b: Vec2, add: boolean): void {
  if (Math.abs(b.x - a.x) * paperView().scale < 4 && Math.abs(b.y - a.y) * paperView().scale < 4) return; // a click
  const got = allSheetObjects(sh, SHEET_OBJECTS).filter((s) => marqueeTakes(a, b, schObjectPath(sh, s)?.ring ?? []));
  const next = add ? [...schemSelSet, ...got.filter((s) => !schemSelHas(s))] : got;
  setSchemSelSet(next);
}
/** Active tool while a schematics sheet is in view. */
export type SchTool =
  | 'connection'
  | 'dim'
  | 'elecBox'
  | 'elecWire'
  | 'ellipse'
  | 'leader'
  | 'line'
  | 'rect'
  | 'select'
  | 'text'
  | 'viewport';
export let schTool: SchTool = 'select';
/** The line tool is drawing a closed ring (the sheet's Polygon button). */
export let schLineClosed = false;
export function setSchTool(t: SchTool, closed = false): void {
  schTool = t;
  elecSheet.elecWireDraft = t === 'elecWire' ? {} : null;
  schLineClosed = t === 'line' && closed;
  sheetSchematic.schDimDraft = null;
  sheetSchematic.schLineDraft = [];
  sheetSchematic.schConnDraft = null;
  sheetSchematic.schBoxDraft = null;
  state.distEntry = null; updateDistEntry(); // the entry belonged to the draft just cleared
  for (const [id, name] of [['btn-sch-text', 'text'], ['btn-rectangle', 'rect'], ['btn-circle', 'ellipse'], ['btn-leader', 'leader'], ['btn-dimension', 'dim'], ['btn-sch-viewport', 'viewport'], ['btn-sch-conn', 'connection'], ['btn-sch-elec-box', 'elecBox'], ['btn-sch-elec-wire', 'elecWire']] as const)
    document.getElementById(id)?.classList.toggle('active', schTool === name);
  document.getElementById('btn-sch-line')?.classList.toggle('active', schTool === 'line' && !schLineClosed);
  document.getElementById('btn-polygon')?.classList.toggle('active', schTool === 'line' && schLineClosed);
  canvas.style.cursor = t === 'select' ? 'default' : 'crosshair';
  renderProperties();
  invalidate();
}
/** A paper tool's button: arms it afresh, or — armed already — back to Select. */
export function toggleSchTool(t: SchTool, closed = false): void {
  setSchTool(schTool === t && schLineClosed === (t === 'line' && closed) ? 'select' : t, closed);
}
/** A paper tool has done its job: back to Select — or, with Settings ▸ Keep
 *  drawing tools active, armed again with a fresh draft, as the model's tools are. */
export function endPaperOneShot(): void {
  setSchTool(state.keepToolActive ? schTool : 'select', schLineClosed);
}

/** Insert an image/PDF onto the active schematic sheet via a file picker (drag &
 *  drop from the OS file explorer is the other path). */
export function insertSchematicImage(): void {
  const sh = activeSheet();
  if (!sh) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,application/pdf';
  input.addEventListener('change', () => { const f = input.files?.[0]; if (f) { sheetSpace.schDropScreen = null; void importDroppedFile(f); } });
  input.click();
}

/** Place an imported image on a schematic sheet, centred on the drop point (or
 *  the page centre), sized to fit (capped to 150 mm wide, aspect preserved). */
export function addSchematicImageFromData(sh: Sheet, dataUrl: string, wIn: number, hIn: number): void {
  let w = wIn > 0 ? wIn : 100, h = hIn > 0 ? hIn : 75;
  const maxW = 150;
  if (w > maxW) { const s = maxW / w; w *= s; h *= s; }
  const at = sheetSpace.schDropScreen ? screenToPaper(paperView(), sheetSpace.schDropScreen) : { x: sh.widthMm / 2, y: sh.heightMm / 2 };
  pushHistory();
  const im: SchematicImage = { id: schUid('img'), x: at.x - w / 2, y: at.y - h / 2, w, h, dataUrl };
  schContent(sh).images.push(im);
  setSchemSel({ kind: 'image', id: im.id });
  setSchTool('select');
  renderProperties(); invalidate();
}

/** Each viewport's model as last painted on screen (device px), drawn again only
 *  when what it shows changed: dragging a box, a table or a viewport on the
 *  sheet, or selecting one, paints no model. Only the active sheet's are kept. */
const viewportBitmaps = new Map<string, { key: string; canvas: HTMLCanvasElement }>();
const serials = new WeakMap<object, number>();
let serialNext = 0;
/** An object's serial: the same object, the same number. */
function serialOf(o: object): number {
  let n = serials.get(o);
  if (n === undefined) serials.set(o, n = ++serialNext);
  return n;
}
/** The options that carry the drawing's own data (its revision stands for them). */
const VIEWPORT_DRAWN = new Set(['floorings', 'paint', 'polygons', 'revision', 'texts', 'walls']);
/** What a viewport's model looks like, its window aside: the model (the drawing's
 *  revision, its sheets left out, and the catalogue) and every option it is drawn
 *  with — a set as its members, an image as its serial and size (an image is in
 *  the options once loaded), a long string (an image's data, the drawing's) not
 *  at all. An option added to `sheetRenderOpts` is in it with no more ado. */
function viewportModelKey(opts: RenderOptions): string {
  const optsKey = JSON.stringify(opts, (k, v: unknown) => {
    if (VIEWPORT_DRAWN.has(k) || (typeof v === 'string' && v.length > 1024)) return undefined;
    if (v instanceof Set) return [...v].sort();
    if (v instanceof Map) return [...v.values()];
    if (v instanceof HTMLImageElement) return `img${serialOf(v)}:${v.naturalWidth}`;
    if (v instanceof HTMLCanvasElement || v instanceof ImageBitmap) return `bitmap${serialOf(v)}`;
    return v;
  });
  return `${changes.revisionExcept(state.drawing, ['sheets'])}|${templatesRevision}|${optsKey}`;
}
/** A viewport's model on screen: its bitmap, painted again when the model, the
 *  options or its window (scale, centre, size and sub-pixel place on screen)
 *  changed, then laid on whole device pixels — what painting it straight away
 *  would have made. */
function paintViewportModel(c: CanvasRenderingContext2D, v: SheetViewport, r: { x: number; y: number; w: number; h: number }, out: SheetOutput, modelKey: string): void {
  const dpr = c.getTransform().a || 1;
  const x0 = Math.floor(r.x * dpr), y0 = Math.floor(r.y * dpr);
  const W = Math.max(1, Math.ceil((r.x + r.w) * dpr) - x0), H = Math.max(1, Math.ceil((r.y + r.h) * dpr) - y0);
  const m = vpModelViewport(out.modelPv, v);
  m.tx -= x0 / dpr;
  m.ty -= y0 / dpr;
  const key = `${modelKey}|${W}x${H}@${dpr}|${m.scale}|${m.tx.toFixed(4)},${m.ty.toFixed(4)}`;
  let kept = viewportBitmaps.get(v.id);
  if (kept?.key !== key) {
    const canvas = kept?.canvas ?? document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const cx = canvas.getContext('2d');
    if (!cx) { render(c, vpModelViewport(out.modelPv, v), state.drawing, out.opts); return; }
    render(cx, m, state.drawing, { ...out.opts, deviceScale: dpr });
    kept = { key, canvas };
    viewportBitmaps.set(v.id, kept);
  }
  c.save();
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.drawImage(kept.canvas, x0, y0);
  c.restore();
}

/** Render the active sheet: each viewport shows the model at its scale, clipped. */
/** How a sheet is painted for one output (`paintSheet`): on screen — live, with
 *  the selection and the box being edited — or in print, at device px. */
interface SheetOutput {
  /** Paper → this output's px: the clips, the schematic, the tables, the title block. */
  pv: PaperView;
  /** What the model viewports project through (print: its reference scale, which
   *  render() thickens by `opts.deviceScale`). */
  modelPv: PaperView;
  opts: RenderOptions;
  ink: string;
  live: boolean;
  logo: (cart: Cartiglio) => HTMLImageElement | null;
  /** A viewport's frame and scale label (screen), after its model. */
  framed?: (v: SheetViewport, r: { x: number; y: number; w: number; h: number }) => void;
}

type SheetLayer =
  | 'elec'
  | 'revisions'
  | 'schedules'
  | 'schematic'
  | 'titleBlock'
  | 'viewports';
/** A sheet's layers, bottom to top in this literal's order — the ONE list the
 *  screen, print, PDF and the thumbnails paint (a layer added to one output only
 *  showed on screen and not in the PDF, or the other way round). */
const SHEET_PAINT: Record<SheetLayer, (c: CanvasRenderingContext2D, sh: Sheet, out: SheetOutput) => void> = {
  viewports: (c, sh, out) => {
    if (out.live) for (const id of viewportBitmaps.keys()) if (!sh.viewports.some((v) => v.id === id)) viewportBitmaps.delete(id);
    const modelKey = out.live ? viewportModelKey(out.opts) : '';
    for (const v of sh.viewports) {
      const r = vpScreenRect(out.pv, v);
      c.save();
      c.beginPath(); c.rect(r.x, r.y, r.w, r.h); c.clip();
      if (out.live) paintViewportModel(c, v, r, out, modelKey);
      else render(c, vpModelViewport(out.modelPv, v), state.drawing, out.opts);
      c.restore();
      out.framed?.(v, r);
    }
  },
  elec: (c, sh, out) => drawSheetElecViews(c, out.pv, sh, out.ink, out.live),
  schematic: (c, sh, out) => {
    if (!sh.schematic) return;
    if (!out.live) { renderSchematic(c, out.pv, sh, out.ink, [], () => { /* print is one-shot */ }); return; }
    const boxEdit = schEditBoxId ? { boxId: schEditBoxId, caret: textEditor.selectionEnd ?? textEditor.value.length, on: schCaretOn } : null;
    renderSchematic(c, out.pv, sh, out.ink, schemSelSet, () => invalidate(), boxEdit);
  },
  revisions: (c, sh, out) => { if (sh.showRevisionTable && sh.revisionTableRect) drawRevisionTable(c, out.pv, sh, state.drawing.revisions || [], curLang()); },
  schedules: (c, sh, out) => { for (const t of sheetScheduleTables(sh)) drawScheduleTablePaper(c, out.pv, t.rect, t.title, t.headers, t.fracs, t.rows, t.empty); },
  titleBlock: (c, sh, out) => { const cart = cartiglioForSheet(sh); if (cart) drawTitleBlock(c, out.pv, sh, cart, state.drawing.meta, out.logo(cart), curLang()); },
};
const SHEET_LAYERS = Object.keys(SHEET_PAINT) as SheetLayer[];

/** A sheet painted for one output: its tables laid out, then its layers in order. */
function paintSheet(c: CanvasRenderingContext2D, sh: Sheet, out: SheetOutput): void {
  layoutSheetTables(sh);
  for (const layer of SHEET_LAYERS) SHEET_PAINT[layer](c, sh, out);
}

/** The tables' heights from their wrapped rows — laid out before a sheet is
 *  painted, so no painter writes the data it draws. */
function layoutSheetTables(sh: Sheet): void {
  for (const t of sheetTables(sh)) t.rect.h = t.heightAt(t.rect.w);
}

/** A sheet's placed tables — its changes table, then its schedule tables — each
 *  with the height its rows wrap to at a width: what a press on one moves or
 *  widens (one drag for both kinds) and what the layout sets. */
function sheetTables(sh: Sheet): Array<{ rect: { x: number; y: number; w: number; h: number }; heightAt: (w: number) => number }> {
  const out: Array<{ rect: { x: number; y: number; w: number; h: number }; heightAt: (w: number) => number }> = [];
  if (sh.showRevisionTable && sh.revisionTableRect) out.push({ rect: sh.revisionTableRect, heightAt: (w) => revisionTableHeightMm(w, state.drawing.revisions || [], curLang()) });
  for (const t of sheetScheduleTables(sh)) out.push({ rect: t.rect, heightAt: (w) => scheduleTableHeightMm(w, t.fracs, t.rows) });
  return out;
}

/** The table under a screen point and what a press there does: its edges widen
 *  it (5 px either side), its body moves it. The tables are drawn over the
 *  schematic's views and the sheet's own objects, so what is under one is not
 *  pressed (elecViewAt, sheetMouseDown). */
export function sheetTableAt(sh: Sheet, pv: PaperView, pos: Vec2): { t: ReturnType<typeof sheetTables>[number]; mode: 'move' | 'resize-w' | 'resize-w-left' } | null {
  for (const t of sheetTables(sh)) {
    const tl = paperToScreen(pv, t.rect.x, t.rect.y);
    const sr = { x: tl.x, y: tl.y, w: t.rect.w * pv.scale, h: t.rect.h * pv.scale };
    const tol = 5;
    const inY = pos.y >= sr.y - tol && pos.y <= sr.y + sr.h + tol;
    if (inY && Math.abs(pos.x - (sr.x + sr.w)) <= tol) return { t, mode: 'resize-w' };
    if (inY && Math.abs(pos.x - sr.x) <= tol) return { t, mode: 'resize-w-left' };
    if (pos.x >= sr.x && pos.x <= sr.x + sr.w && pos.y >= sr.y && pos.y <= sr.y + sr.h) return { t, mode: 'move' };
  }
  return null;
}

export function renderSheet(sh: Sheet): void {
  const cw = viewW(), ch = viewH();
  const dpr = canvas.width / cw || 1;
  const pv = paperViewOf(sh, cw, ch, sheetSpace.paperCam);
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cw, ch); // transparent — the white page shows through from the bg canvas
  paintSheet(ctx, sh, {
    pv, modelPv: pv, opts: sheetRenderOpts(sh), ink: '#1b1d22', live: true,
    logo: (cart) => cartiglioLogo(cart.logoDataUri),
    // A viewport's frame and plot scale, after its model.
    framed: (v, r) => {
      const active = schemSelHas({ kind: 'viewport', id: v.id });
      ctx.strokeStyle = sheetSpace.sheetEnterId === v.id ? '#4db891' : active ? '#4aa3ff' : '#8a8f98';
      ctx.lineWidth = active || sheetSpace.sheetEnterId === v.id ? 2 : 1;
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillText(scaleLabel(v.scale), r.x + 5, r.y + r.h - 6);
    },
  });
  // Over the sheet: the draft being drawn, the alignment guides, the title
  // block's master view and a selected viewport's grips.
  drawSchematicDrafts(pv); // draft box/line/connection preview (checks its own state)
  if (state.alignGuides.length) {
    // Purple dashed lines linking a dragged box's edge to the box it aligns to.
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.95)';
    ctx.lineWidth = 1;
    for (const [a, b] of state.alignGuides) {
      const sa = paperToScreen(pv, a.x, a.y), sb = paperToScreen(pv, b.x, b.y);
      ctx.beginPath(); ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y); ctx.stroke();
    }
    ctx.restore();
  }
  const cart = cartiglioForSheet(sh);
  if (cart) {
    if (titleEditor.titleEdit) {
      // Outline every object faintly; every selected one gets a bold box, and
      // resize handles only show when exactly one object is selected.
      ctx.strokeStyle = 'rgba(74,163,255,0.35)'; ctx.lineWidth = 1;
      for (const o of cart.objects) { const a = paperToScreen(pv, o.x, o.y); ctx.strokeRect(a.x, a.y, o.w * pv.scale, o.h * pv.scale); }
      const selObjs = selectedTitleObjs(cart);
      ctx.strokeStyle = '#4aa3ff'; ctx.lineWidth = 1.5;
      for (const o of selObjs) { const a = paperToScreen(pv, o.x, o.y); ctx.strokeRect(a.x, a.y, o.w * pv.scale, o.h * pv.scale); }
      if (selObjs.length === 1) drawGrips(ctx, titleObjHandles(pv, selObjs[0]));
      // Live L/R/T/B distance-to-nearest-edge dimension lines, while dragging.
      for (const d of titleEditor.titleObjDims) {
        const p0 = paperToScreen(pv, d.from.x, d.from.y), p1 = paperToScreen(pv, d.to.x, d.to.y);
        ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke();
        const tick = 4;
        for (const p of [p0, p1]) {
          ctx.beginPath();
          if (d.side === 'l' || d.side === 'r') { ctx.moveTo(p.x, p.y - tick); ctx.lineTo(p.x, p.y + tick); }
          else { ctx.moveTo(p.x - tick, p.y); ctx.lineTo(p.x + tick, p.y); }
          ctx.stroke();
        }
        const label = `${Math.round(d.mm)} mm`;
        const mx = (p0.x + p1.x) / 2, my = (p0.y + p1.y) / 2;
        ctx.font = '600 11px system-ui, sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(30,33,40,0.92)';
        ctx.fillRect(mx - tw / 2 - 4, my - 9, tw + 8, 16);
        ctx.fillStyle = '#ffd166'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(label, mx, my + 1);
        ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      }
      if (titleEditor.titleReadout && titleEditor.titleReadout.text) {
        ctx.font = '600 12px system-ui, sans-serif';
        const tw = ctx.measureText(titleEditor.titleReadout.text).width;
        const bx = titleEditor.titleReadout.at.x + 14, by = titleEditor.titleReadout.at.y - 26;
        ctx.fillStyle = 'rgba(30,33,40,0.92)'; ctx.fillRect(bx, by, tw + 12, 20);
        ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        ctx.fillText(titleEditor.titleReadout.text, bx + 6, by + 10); ctx.textBaseline = 'alphabetic';
      }
    }
  }
  const sel = selectedViewport();
  if (sel && sheetSpace.sheetEnterId !== sel.id && !viewportLocked(sel)) drawGrips(ctx, vpHandles(pv, sel));
  ctx.restore();
}

export function setViewTab(tab: string): void {
  // What is under way goes back where it began: a drag, a handle's or a grip's
  // work (each worked in the view it was pressed in).
  cancelUnderWay();
  if (tab === state.viewTab) { if (tab === '3d') openView3d(); return; }
  // The drawing editor works on the plan's canvas: it is finished (Done) or
  // cancelled first, never left behind on another view.
  if (state.editing?.kind === 'part') { flashToolStatus(tr('Finish the drawing editor first: Done or Cancel.')); return; }
  if (titleEditor.titleEdit) { titleEditor.titleEdit = false; selectTitleObjOnly(null); void commitTitleEdit(); renderProductList(); } // save title-block edits on leave
  const modelSpace = (t: string): boolean => t === 'model' || t === '3d';
  const keepSelection = modelSpace(state.viewTab) && modelSpace(tab);
  if (tab === '3d') leavePlanTools(); // while the plan is still the view: a re-armed tool stays on it
  else if (state.tool === 'section-3d') setTool('select'); // it works in 3D only
  state.viewTab = tab;
  setSchemSel(null);
  sheetSpace.sheetEnterId = null;
  sheetSpace.sheetDrag = null;
  // A selection is model-space: it stays between the plan and 3D, and is dropped
  // on a sheet, where it would linger invisibly.
  if (!keepSelection) deselectAll();
  resetPaperView();
  if (tab === '3d') openView3d(); else closeView3d();
  canvas.style.cursor = 'default';
  const sh = activeSheet();
  document.body.classList.toggle('sheet-mode', !!sh);
  setSchemSel(null);
  elecSheet.elecDrag = null;
  if (sh) setSchTool('select'); // sheets use the schematic (annotation) toolbar
  setToolbarMode(sh ? 'schematic' : 'drawing');
  renderViewTabs();
  renderProperties();
  redrawBackdrop();
  invalidate();
}

/** Keep a viewport inside the sheet page. Viewports are NOT constrained by the
 *  title block (cartiglio) or the changes table — they may freely overlap
 *  either; they still snap to their edges while dragging. */
export function clampViewportToContent(vp: SheetViewport, sh: Sheet): void {
  const a = { x0: 0, y0: 0, x1: sh.widthMm, y1: sh.heightMm };
  vp.w = Math.min(vp.w, a.x1 - a.x0);
  vp.h = Math.min(vp.h, a.y1 - a.y0);
  vp.x = Math.max(a.x0, Math.min(vp.x, a.x1 - vp.w));
  vp.y = Math.max(a.y0, Math.min(vp.y, a.y1 - vp.h));
}

const VP_MARGIN = 5; // page margin (mm) viewports snap to, inset from each sheet edge
interface SnapResult { value: number; snapped: boolean; dist: number; }
/** Nearest candidate to `v` within `tol`, flagged so callers can tell a real
 *  snap apart from the unchanged-input fallback (both can have zero drift). */
function snapToCandidates(v: number, candidates: number[], tol: number): SnapResult {
  let best = v, bestD = tol, snapped = false;
  for (const c of candidates) { const d = Math.abs(c - v); if (d < bestD) { bestD = d; best = c; snapped = true; } }
  return { value: best, snapped, dist: snapped ? bestD : Infinity };
}
/** The x/y edges viewports snap to: every title-block object's edges, the
 *  changes table's edges (if shown), plus the 5 mm inset page margin on each
 *  side. */
function viewportSnapEdges(sh: Sheet, cart: Cartiglio | null, includeTable = true): { cx: number[]; cy: number[] } {
  const cx: number[] = [VP_MARGIN, sh.widthMm - VP_MARGIN];
  const cy: number[] = [VP_MARGIN, sh.heightMm - VP_MARGIN];
  for (const o of cart?.objects || []) { cx.push(o.x, o.x + o.w); cy.push(o.y, o.y + o.h); }
  if (includeTable && sh.showRevisionTable && sh.revisionTableRect) {
    const t = sh.revisionTableRect;
    cx.push(t.x, t.x + t.w); cy.push(t.y, t.y + t.h);
  }
  return { cx, cy };
}
/** Snap a moving viewport's proposed top-left (paper mm) so either its left/right
 *  edge lands on a title-block edge or the 5 mm margin, and likewise top/bottom. */
function snapViewportMove(sh: Sheet, cart: Cartiglio | null, vp: SheetViewport, nx: number, ny: number): Vec2 {
  const tol = SNAP_PX / paperView().scale;
  const { cx, cy } = viewportSnapEdges(sh, cart);
  const left = snapToCandidates(nx, cx, tol), right = snapToCandidates(nx + vp.w, cx, tol);
  let x = nx;
  if (left.snapped || right.snapped) x = (right.snapped && (!left.snapped || right.dist < left.dist)) ? right.value - vp.w : left.value;
  const top = snapToCandidates(ny, cy, tol), bottom = snapToCandidates(ny + vp.h, cy, tol);
  let y = ny;
  if (top.snapped || bottom.snapped) y = (bottom.snapped && (!top.snapped || bottom.dist < top.dist)) ? bottom.value - vp.h : top.value;
  return { x, y };
}
/** Snap whichever viewport edge(s) a resize handle just moved to a title-block
 *  edge or the 5 mm margin. */
function snapViewportResize(sh: Sheet, cart: Cartiglio | null, vp: SheetViewport, handle: HandleId): void {
  const tol = SNAP_PX / paperView().scale;
  const { cx, cy } = viewportSnapEdges(sh, cart);
  if (handle.includes('w')) { const s = snapToCandidates(vp.x, cx, tol); if (s.snapped) { vp.w += vp.x - s.value; vp.x = s.value; } }
  if (handle.includes('e')) { const s = snapToCandidates(vp.x + vp.w, cx, tol); if (s.snapped) vp.w = s.value - vp.x; }
  if (handle.includes('n')) { const s = snapToCandidates(vp.y, cy, tol); if (s.snapped) { vp.h += vp.y - s.value; vp.y = s.value; } }
  if (handle.includes('s')) { const s = snapToCandidates(vp.y + vp.h, cy, tol); if (s.snapped) vp.h = s.value - vp.y; }
}

/** Size a viewport to fill the sheet's usable area — the region clear of the
 *  title blocks, inset by a 5 mm print margin from every sheet edge. This is a
 *  one-time fit at sheet creation; the viewport is never re-sized afterwards if
 *  the title blocks change. */
/** The largest empty rectangle on a sheet — the white area clear of the title
 *  blocks, inset 5 mm from every edge. It's one of two full-span strips rather
 *  than the intersection of every title-block reservation: shrinking around a
 *  corner block into a single quadrant would leave a big empty margin (e.g. a
 *  wide gap to the right of a bottom-right block).
 *    A = full width, height cleared of the title blocks above/below it;
 *    B = full height, width cleared of the blocks left/right of it.
 *  Each object clears whichever axis-side (by its centre) it sits on; the
 *  larger-area strip wins, so a bottom/right block gives back the full width. */
export function sheetWhiteArea(sh: Sheet): { x0: number; y0: number; x1: number; y1: number } {
  const M = 5; // print margin (mm) from every sheet edge
  const W = sh.widthMm, H = sh.heightMm;
  const objs = cartiglioForSheet(sh)?.objects ?? [];
  let ay0 = M, ay1 = H - M, bx0 = M, bx1 = W - M;
  for (const o of objs) {
    if (o.y + o.h / 2 < H / 2) ay0 = Math.max(ay0, o.y + o.h); else ay1 = Math.min(ay1, o.y);
    if (o.x + o.w / 2 < W / 2) bx0 = Math.max(bx0, o.x + o.w); else bx1 = Math.min(bx1, o.x);
  }
  const A = { x0: M, x1: W - M, y0: ay0, y1: ay1 };
  const B = { x0: bx0, x1: bx1, y0: M, y1: H - M };
  const area = (r: { x0: number; y0: number; x1: number; y1: number }): number => Math.max(0, r.x1 - r.x0) * Math.max(0, r.y1 - r.y0);
  return area(A) >= area(B) ? A : B;
}

export function fitDefaultViewport(vp: SheetViewport, sh: Sheet): void {
  const r = sheetWhiteArea(sh);
  vp.x = r.x0;
  vp.y = r.y0;
  vp.w = Math.max(20, r.x1 - r.x0);
  vp.h = Math.max(20, r.y1 - r.y0);
  const b = modelBoundsOrNull();
  if (b) fitViewportToBounds(vp, b);
}

/** A room as the plan labels it, in `lang` (a lighting plan sheet's default names it). */
export function roomPlanLabel(id: string, lang: Lang): string | undefined {
  return numberedNames(state.drawing.instances, (i) => productName(i, lang)).get(id);
}
/** A sheet's name in `lang` (the UI's: a tab, the strip, the panel). */
export function sheetLabel(sh: Sheet, lang: Lang = getUiLang()): string {
  return sheetName(sh, lang, trIn, roomPlanLabel);
}

// ---- Sheet pointer interaction (only while a sheet tab is active) ----
/** A press on a table — the changes table, then the schedule tables: its body
 *  drags it, an edge widens it (its height always fits its rows). */
function sheetTableMouseDown(sh: Sheet, pv: PaperView, pos: Vec2): boolean {
  const at = sheetTableAt(sh, pv, pos);
  if (!at) return false;
  const { t, mode } = at;
  beginGesture();
  sheetTableDrag = { rect: t.rect, heightAt: t.heightAt, mode, startScreen: pos, sx: t.rect.x, sy: t.rect.y, sw: t.rect.w };
  return true;
}

export function sheetMouseDown(e: MouseEvent): void {
  const pos = screenPos(e);
  const sh = activeSheet(); if (!sh) return;
  const pv = paperView();
  sheetHover = null;
  // Space-drag or middle-drag pans the page, whatever is under the cursor.
  if (e.button === 1 || (e.button === 0 && spaceHeld)) {
    noteSpacePan();
    sheetSpace.sheetDrag = { mode: 'paper', startScreen: pos, sx: sheetSpace.paperCam.pan.x, sy: sheetSpace.paperCam.pan.y, sw: 0, sh: 0, cx: 0, cy: 0 };
    return;
  }
  // A selected viewport's resize handles take priority over the schematic
  // annotation tools, so a viewport stays resizable even while a create tool
  // (text/line/connection) is active on the unified sheet toolbar.
  if (!titleEditor.titleEdit && !sheetSpace.sheetEnterId) {
    const selVp = selectedViewport();
    if (selVp && !viewportLocked(selVp)) {
      for (const h of vpHandles(pv, selVp)) {
        if (Math.abs(pos.x - h.x) <= GRIP_HIT_PX && Math.abs(pos.y - h.y) <= GRIP_HIT_PX) {
          beginGesture();
          sheetSpace.sheetDrag = { mode: 'resize', handle: h.id, startScreen: pos, sx: selVp.x, sy: selVp.y, sw: selVp.w, sh: selVp.h, cx: selVp.centerWorld.x, cy: selVp.centerWorld.y };
          return;
        }
      }
    }
  }
  // A table is drawn over the views and the sheet's own objects: a Select press
  // on it takes it, after the selected object's grips (drawn over everything).
  if (!titleEditor.titleEdit && e.button === 0 && schTool === 'select' && !schHandleAt(sh, pv, pos) && !schVertexAt(sh, pv, pos) && sheetTableMouseDown(sh, pv, pos)) return;
  // Schematic objects (boxes/lines/connections/images) live on every sheet and
  // take priority; a Select-tool click on empty space falls through to the
  // viewport / page handling below.
  if (!titleEditor.titleEdit && elecMouseDown(e, sh, pv)) return;
  if (!titleEditor.titleEdit && schematicMouseDown(e, sh, pv)) return;
  if (titleEditor.titleEdit) {
    const cart = cartiglioForSheet(sh);
    if (cart) { // object-model editor: select / move / resize
      const selO = selectedTitleObj(cart);
      if (selO && titleEditor.selectedTitleObjIds.length === 1) {
        const hh = hitTitleObjHandle(pv, selO, pos);
        if (hh) { titleEditor.titleObjDrag = { mode: 'resize', handle: hh, id: selO.id, startScreen: pos, obj: JSON.parse(JSON.stringify(selO)) as TitleObject, moved: false }; return; }
      }
      const hit = titleObjAt(cart, screenToPaper(pv, pos));
      if (e.shiftKey) {
        if (hit) toggleTitleObjSelection(hit.id);
      } else if (hit && !titleEditor.selectedTitleObjIds.includes(hit.id)) {
        selectTitleObjOnly(hit.id); // clicked outside the current multi-selection → replace it
      } else if (!hit) {
        selectTitleObjOnly(null);
      } // else: clicked a member of the existing multi-selection → keep it, drag the whole group
      if (hit && titleEditor.selectedTitleObjIds.includes(hit.id)) { // not a shift-click that just deselected it
        const objs = selectedTitleObjs(cart).map((o) => ({ id: o.id, x: o.x, y: o.y }));
        titleEditor.titleObjDrag = { mode: 'move', startScreen: pos, objs, moved: false };
      }
      renderProperties();
      invalidate();
      return;
    }
  }
  // A table pressed with another tool, which the schematic's left alone.
  if (!titleEditor.titleEdit && sheetTableMouseDown(sh, pv, pos)) return;
  if (sheetSpace.sheetEnterId) { // panning the model inside the entered viewport
    const v = sh.viewports.find((x) => x.id === sheetSpace.sheetEnterId);
    const r = v ? vpScreenRect(pv, v) : null;
    const inside = !!r && pos.x >= r.x && pos.x <= r.x + r.w && pos.y >= r.y && pos.y <= r.y + r.h;
    if (v && inside && !viewportLocked(v)) { beginGesture(); sheetSpace.sheetDrag = { mode: 'pan', startScreen: pos, sx: v.x, sy: v.y, sw: v.w, sh: v.h, cx: v.centerWorld.x, cy: v.centerWorld.y }; return; }
    // Clicked outside the entered viewport → leave pan mode and fall through to
    // normal handle / selection handling.
    sheetSpace.sheetEnterId = null;
  }
  // (Resize handles of the selected viewport are handled up top, before the
  // schematic tools, so a viewport stays resizable in any tool mode.)
  const hit = viewportAt(pv, sh, pos);
  if (hit && (e.shiftKey || e.ctrlKey || e.metaKey)) toggleSchemSel({ kind: 'viewport', id: hit.id });
  else setSchemSel(hit ? { kind: 'viewport', id: hit.id } : null);
  if (sheetSpace.sheetEnterId && sheetSpace.sheetEnterId !== hit?.id) sheetSpace.sheetEnterId = null;
  // A locked viewport is only selectable (so it can be unlocked); it never moves.
  if (hit && !viewportLocked(hit)) { beginGesture(); sheetSpace.sheetDrag = { mode: 'move', startScreen: pos, sx: hit.x, sy: hit.y, sw: hit.w, sh: hit.h, cx: hit.centerWorld.x, cy: hit.centerWorld.y }; }
  else if (!hit && e.button === 0) { sheetSchematic.sheetMarquee = { a: screenToPaper(pv, pos), b: screenToPaper(pv, pos), add: false }; } // a marquee (Space / middle-drag pans)
  renderProperties();
  invalidate();
}

export function sheetMouseMove(e: MouseEvent): void {
  const sh = activeSheet(); if (!sh) return;
  const pos = screenPos(e);
  const pv = paperView();
  if (sheetSchematic.sheetMarquee) { sheetSchematic.sheetMarquee.b = screenToPaper(pv, pos); invalidate(); return; }
  // Hover outline: what a Select click here would pick (not in the title editor,
  // not over a grip, not what is already selected).
  if (!titleEditor.titleEdit) {
    let hv: typeof sheetHover = null;
    if (schTool === 'select' && !schDrag && !sheetSpace.sheetDrag && e.buttons === 0 && e.target === canvas && !spaceHeld
        && !schHandleAt(sh, pv, pos) && !schVertexAt(sh, pv, pos)) {
      const hit = schHitObject(sh, screenToPaper(pv, pos), 3 / pv.scale);
      const path = hit && !schemSelHas(hit) ? schObjectPath(sh, hit) : null;
      if (hit && path) hv = { key: schemSelKey(hit), ...path };
    }
    if ((hv?.key ?? '') !== (sheetHover?.key ?? '')) invalidate();
    sheetHover = hv;
  }
  if (!titleEditor.titleEdit) { elecWireHover(pv, pos); elecAutoTip(sh, pv, pos); }
  if (!titleEditor.titleEdit && elecMouseMove(e, sh, pv)) return;
  if (!titleEditor.titleEdit && schematicMouseMove(e, sh, pv)) return;
  if (sheetTableDrag) {
    const d = sheetTableDrag, rect = d.rect;
    // Snap the table's edges to the same targets viewports use — title-block
    // edges and the 5 mm page margin — plus the other placed tables, excluding
    // its own edges.
    const tol = SNAP_PX / pv.scale;
    const base = viewportSnapEdges(sh, cartiglioForSheet(sh), false);
    const others = otherTableEdges(sh, rect);
    const cx = [...base.cx, ...others.cx], cy = [...base.cy, ...others.cy];
    const dx = (pos.x - d.startScreen.x) / pv.scale, dy = (pos.y - d.startScreen.y) / pv.scale;
    if (d.mode === 'move') {
      let nx = d.sx + dx, ny = d.sy + dy;
      const left = snapToCandidates(nx, cx, tol), right = snapToCandidates(nx + rect.w, cx, tol);
      if (left.snapped || right.snapped) nx = right.snapped && (!left.snapped || right.dist < left.dist) ? right.value - rect.w : left.value;
      const top = snapToCandidates(ny, cy, tol), bottom = snapToCandidates(ny + rect.h, cy, tol);
      if (top.snapped || bottom.snapped) ny = bottom.snapped && (!top.snapped || bottom.dist < top.dist) ? bottom.value - rect.h : top.value;
      rect.x = nx; rect.y = ny;
    } else if (d.mode === 'resize-w') {
      let rightX = d.sx + d.sw + dx;
      const s = snapToCandidates(rightX, cx, tol); if (s.snapped) rightX = s.value;
      rect.w = Math.max(40, rightX - rect.x);
    } else {
      let leftX = d.sx + dx;
      const s = snapToCandidates(leftX, cx, tol); if (s.snapped) leftX = s.value;
      const rightX = d.sx + d.sw;
      rect.x = Math.min(leftX, rightX - 40);
      rect.w = rightX - rect.x;
    }
    // Re-fit the height to the width, so wrapped rows grow / shrink live.
    rect.h = d.heightAt(rect.w);
    rect.x = Math.max(0, Math.min(rect.x, sh.widthMm - rect.w));
    rect.y = Math.max(0, Math.min(rect.y, sh.heightMm - rect.h));
    invalidate();
    return;
  }
  if (titleEditor.titleObjDrag) {
    const drag = titleEditor.titleObjDrag;
    const cart = cartiglioForSheet(sh);
    if (!cart) return;
    const dxMm = (pos.x - drag.startScreen.x) / pv.scale, dyMm = (pos.y - drag.startScreen.y) / pv.scale;
    if (!drag.moved && Math.abs(pos.x - drag.startScreen.x) + Math.abs(pos.y - drag.startScreen.y) > 2) {
      pushTitleObjHistory(); drag.moved = true;
    }
    if (drag.mode === 'move') {
      const primaryStart = drag.objs.find((s) => s.id === titleEditor.selectedTitleObjId) ?? drag.objs[0];
      const primary = cart.objects?.find((x) => x.id === primaryStart.id);
      if (!primary) return;
      const excludeIds = new Set(drag.objs.map((s) => s.id));
      const snapped = snapTitleObjMove(sh, cart, primary, primaryStart.x + dxMm, primaryStart.y + dyMm, excludeIds);
      const appliedDx = snapped.x - primaryStart.x, appliedDy = snapped.y - primaryStart.y;
      for (const s of drag.objs) {
        const o = cart.objects?.find((x) => x.id === s.id); if (!o) continue;
        o.x = Math.round(s.x + appliedDx); o.y = Math.round(s.y + appliedDy);
      }
      titleEditor.titleReadout = drag.objs.length > 1
        ? { text: `${drag.objs.length} blocks · ${Math.round(primary.x)}, ${Math.round(primary.y)} mm`, at: pos }
        : { text: `${Math.round(primary.x)}, ${Math.round(primary.y)} mm`, at: pos };
      titleEditor.titleObjDims = titleObjSideDims(sh, cart, primary);
    } else {
      const o = cart.objects?.find((x) => x.id === drag.id);
      if (!o) return;
      resizeTitleObj(o, drag.obj, drag.handle, screenToPaper(pv, pos));
      titleEditor.titleReadout = { text: `${Math.round(o.w)} × ${Math.round(o.h)} mm`, at: pos };
      titleEditor.titleObjDims = titleObjSideDims(sh, cart, o);
    }
    titleEditor.titleEditDirty = true; redrawBackdrop(); invalidate();
    return;
  }
  if (!sheetSpace.sheetDrag) { updateSheetCursor(sh, pos); return; }
  const dsx = pos.x - sheetSpace.sheetDrag.startScreen.x, dsy = pos.y - sheetSpace.sheetDrag.startScreen.y;
  if (sheetSpace.sheetDrag.mode === 'move') {
    const sel = selectedViewport(); if (!sel) return;
    const cart = cartiglioForSheet(sh);
    const snapped = snapViewportMove(sh, cart, sel, sheetSpace.sheetDrag.sx + dsx / pv.scale, sheetSpace.sheetDrag.sy + dsy / pv.scale);
    sel.x = snapped.x; sel.y = snapped.y;
    clampViewportToContent(sel, sh);
  } else if (sheetSpace.sheetDrag.mode === 'resize') {
    const sel = selectedViewport(); if (!sel || !sheetSpace.sheetDrag.handle) return;
    resizeViewport(sel, sheetSpace.sheetDrag.handle, { x: (pos.x - pv.ox) / pv.scale, y: (pos.y - pv.oy) / pv.scale });
    snapViewportResize(sh, cartiglioForSheet(sh), sel, sheetSpace.sheetDrag.handle);
    clampViewportToContent(sel, sh);
  } else if (sheetSpace.sheetDrag.mode === 'pan') {
    const v = sh.viewports.find((x) => x.id === sheetSpace.sheetEnterId); if (!v) return;
    v.centerWorld = panViewport(v, { x: sheetSpace.sheetDrag.cx, y: sheetSpace.sheetDrag.cy }, { x: dsx / pv.scale, y: dsy / pv.scale });
  } else if (sheetSpace.sheetDrag.mode === 'paper') {
    sheetSpace.paperCam = { ...sheetSpace.paperCam, pan: { x: sheetSpace.sheetDrag.sx + dsx, y: sheetSpace.sheetDrag.sy + dsy } };
    redrawBackdrop();
  }
  invalidate();
}

const HANDLE_CURSOR: Record<HandleId, string> = {
  nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
  n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
};

/** Cursor feedback on a sheet: resize over a handle, grab inside an entered
 *  viewport, move over a viewport, default otherwise. */
function updateSheetCursor(sh: Sheet, pos: Vec2): void {
  const pv = paperView();
  if (spaceHeld) { canvas.style.cursor = 'grab'; return; }
  if (titleEditor.titleEdit) {
    const cart = cartiglioForSheet(sh);
    if (cart) {
      const selO = selectedTitleObj(cart);
      const hh = selO ? hitTitleObjHandle(pv, selO, pos) : null;
      if (hh) { canvas.style.cursor = HANDLE_CURSOR[hh]; return; }
      canvas.style.cursor = titleObjAt(cart, screenToPaper(pv, pos)) ? 'move' : 'default';
    } else {
      canvas.style.cursor = 'default';
    }
    return;
  }
  // Every table — the changes table and the schedule tables — as a press takes it.
  const table = sheetTableAt(sh, pv, pos);
  if (table) { canvas.style.cursor = table.mode === 'move' ? 'move' : 'ew-resize'; return; }
  const sel = selectedViewport();
  if (sel && sheetSpace.sheetEnterId !== sel.id) {
    for (const h of vpHandles(pv, sel)) {
      if (Math.abs(pos.x - h.x) <= GRIP_HIT_PX && Math.abs(pos.y - h.y) <= GRIP_HIT_PX) { canvas.style.cursor = HANDLE_CURSOR[h.id]; return; }
    }
  }
  if (sheetSpace.sheetEnterId) {
    const v = sh.viewports.find((x) => x.id === sheetSpace.sheetEnterId);
    const r = v ? vpScreenRect(pv, v) : null;
    if (r && pos.x >= r.x && pos.x <= r.x + r.w && pos.y >= r.y && pos.y <= r.y + r.h) { canvas.style.cursor = 'grab'; return; }
  }
  // Empty paper takes a marquee now; the page pans with Space- or middle-drag.
  canvas.style.cursor = viewportAt(pv, sh, pos) || elecViewAt(sh, screenToPaper(pv, pos), pv) ? 'move' : 'default';
}

export function sheetMouseUp(): void {
  const shUp = activeSheet();
  if (sheetSchematic.sheetMarquee) {
    const m = sheetSchematic.sheetMarquee;
    sheetSchematic.sheetMarquee = null;
    if (shUp) applySheetMarquee(shUp, m.a, m.b, m.add);
    renderProperties(); invalidate();
    return;
  }
  if (shUp && !titleEditor.titleEdit && elecMouseUp(shUp)) return;
  if (shUp && !titleEditor.titleEdit && (sheetSchematic.schBoxDraft || schDrag || sheetSchematic.schConnDraft) && schematicMouseUp(shUp, paperView())) return;
  // Viewport / table / model-pan drags snapshot on mousedown (beginGesture); record
  // the change here — a bare click that moved nothing leaves the undo stack alone.
  if (sheetSpace.sheetDrag || sheetTableDrag) commitGesture();
  sheetSpace.sheetDrag = null; sheetTableDrag = null;
  if (titleEditor.titleObjDrag) { const moved = titleEditor.titleObjDrag.moved; titleEditor.titleObjDrag = null; if (moved) renderProperties(); }
  if (titleEditor.titleObjDims.length) titleEditor.titleObjDims = [];
  if (titleEditor.titleReadout) { titleEditor.titleReadout = null; invalidate(); }
}

export function sheetDblClick(e: MouseEvent): void {
  const sh = activeSheet(); if (!sh) return;
  const pos = screenPos(e);
  // Schematic objects first: finish a line, edit a box, or add a routing point.
  // If nothing schematic is under the cursor, fall through to viewport handling.
  if (!titleEditor.titleEdit) {
    if (finishPaperWork(sh)) return;
    const paper = screenToPaper(paperView(), pos);
    const hit = sheetTableAt(sh, paperView(), pos) ? null : schHitObject(sh, paper, 3 / paperView().scale); // a table is over them
    if (!hit && elecDblClick(sh, paper)) return;
    if (hit?.kind === 'box') { const b = schGetBox(sh, hit.id); if (b) { setSchemSel(hit); schOpenBoxEditor(b); } return; }
    if (hit?.kind === 'conn') {
      // Insert a routing waypoint on the double-clicked segment.
      const cn = schContent(sh).conns.find((x) => x.id === hit.id);
      const route = cn ? schConnRoute(sh, cn) : null;
      if (cn && route) {
        let best = 0, bd = Infinity;
        for (let s = 0; s < route.length - 1; s++) { const d = distToSegment(paper, route[s], route[s + 1]); if (d < bd) { bd = d; best = s; } }
        cn.waypoints = cn.waypoints ?? [];
        pushHistory();
        cn.waypoints.splice(Math.max(0, Math.min(best, cn.waypoints.length)), 0, paper);
        setSchemSel({ kind: 'conn', id: cn.id });
        renderProperties(); invalidate();
      }
      return;
    }
  }
  const cart = cartiglioForSheet(sh);
  if (cart && titleEditor.titleEdit) { // object-model: double-click a block to edit its text in place
    const o = titleObjAt(cart, screenToPaper(paperView(), pos));
    if (o) {
      selectTitleObjOnly(o.id);
      const a = paperToScreen(paperView(), o.x, o.y), cr = canvas.getBoundingClientRect();
      openTitleObjInlineEditor(o, { x: cr.left + a.x, y: cr.top + a.y, w: o.w * paperView().scale, h: o.h * paperView().scale });
      renderProperties();
      return;
    }
  }
  const hit = viewportAt(paperView(), sh, pos);
  // A locked viewport's view is its plot scale, so it is never entered to pan or
  // zoom (AutoCAD: inside a locked viewport the wheel zooms the page instead).
  if (hit && viewportLocked(hit)) flashStatus(tr('The viewport is locked — unlock it to pan or zoom its view'));
  sheetSpace.sheetEnterId = hit && !viewportLocked(hit) ? hit.id : null;
  if (hit) setSchemSel({ kind: 'viewport', id: hit.id });
  renderProperties();
  invalidate();
}

/** Zoom the sheet page about a screen pivot, keeping the paper point under the
 *  pivot fixed. Shared by the wheel and touch (pinch) handlers. */
export function paperZoomAt(sh: Sheet, pivot: Vec2, factor: number): void {
  sheetSpace.paperCam = zoomPaperAbout(sheetSpace.paperCam, sh, viewW(), viewH(), pivot, factor);
  redrawBackdrop();
}

let sheetWheelGesture = 0; // timestamp of the last wheel tick, to coalesce undo
export function sheetWheel(e: WheelEvent): void {
  const sh = activeSheet(); if (!sh) return;
  e.preventDefault();
  const pos = screenPos(e);
  const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
  const v = sheetSpace.sheetEnterId ? sh.viewports.find((x) => x.id === sheetSpace.sheetEnterId) : undefined;
  if (!v || viewportLocked(v)) { // locked since it was entered: the page zooms, not its view
    // Zoom the whole page about the cursor (keep the paper point under it fixed).
    paperZoomAt(sh, pos, factor);
    invalidate();
    return;
  }
  const now = performance.now();
  if (now - sheetWheelGesture > 400) pushHistory(); // one undo step per zoom gesture
  sheetWheelGesture = now;
  // Zoom the model inside the entered viewport, about the cursor.
  const before = vpModelViewport(paperView(), v).screenToWorld(pos);
  v.scale *= factor;
  const after = vpModelViewport(paperView(), v).screenToWorld(pos);
  v.centerWorld = { x: v.centerWorld.x + (before.x - after.x), y: v.centerWorld.y + (before.y - after.y) };
  if (selectedViewport()?.id === v.id) renderProperties(); // its Scale field follows the wheel
  invalidate();
}

/** A key on a sheet (not in its title block's editor): copy, Enter finishing a
 *  paper tool's draft, a key typed into a text box whose corners are down, Escape
 *  cancelling a draft or deselecting, Delete. True when the key was the sheet's. */
export function sheetKeyDown(e: KeyboardEvent, shKey: Sheet): boolean {
  // Copy the selected sheet object. Paste is NOT handled here — the window
  // 'paste' listener owns Ctrl+V (so an image and an object paste can't both
  // fire from one keystroke), and it routes to the schematic clipboard on a sheet.
  if (matchesShortcut(e, 'copy') && schemSel) { e.preventDefault(); copySchematicSelection(); return true; }
  // A pending typed entry owns Enter / Escape first (commit / cancel the
  // number), exactly as it does for the model draw tools; only once nothing
  // is being typed do they finish or cancel the draft.
  if (e.key === 'Enter' && state.distEntry == null && finishPaperWork(shKey)) { e.preventDefault(); return true; }
  // Once both corners are down, typing a printable character creates the box
  // without a leader and captures the keystroke — same as the model tool.
  if (sheetSchematic.schBoxDraft?.a && sheetSchematic.schBoxDraft.b && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault();
    sheetSchematic.schBoxDraft.leader = undefined; // typing skips the arrow
    const ch = e.key;
    schFinishTextBox(shKey); // opens the inline editor
    textEditorUi.type(ch);
    return true;
  }
  if (e.key === 'Escape' && state.distEntry == null && escapePaper(shKey)) { e.preventDefault(); return true; }
  if (matchesShortcut(e, 'delete') && schemSel) { e.preventDefault(); deleteSchematicSelected(shKey); return true; }
  return false;
}

/** Render a sheet to a 300-dpi PNG and send it to the printer.
 *  Line weights in render.ts are fixed device pixels tuned for the on-screen
 *  paper scale (~PRINT_REF_SCALE px/mm). At 300 dpi (~11.8 px/mm) those same
 *  widths would look ~5× thinner, so we render in a reference-scaled space and
 *  apply a global scale (k) that thickens strokes together with the resolution. */
/** Render a sheet to a flattened 300-dpi PNG data URL sized exactly to its paper
 *  (the shared raster behind both Print and Export-to-PDF). Null if no 2D ctx. */
/** Render a whole sheet (viewports, schematic, tables, title block) into an
 *  offscreen canvas at `dpi`. `deviceScale` scales with the dpi, so a low-dpi
 *  render is a faithful scaled-down sheet (same RELATIVE line weights) — which is
 *  what lets the cloud thumbnail reuse this at a small size. */
export async function renderSheetToCanvas(sh: Sheet, dpi = 300): Promise<HTMLCanvasElement | null> {
  const mmToPx = dpi / 25.4;
  const PRINT_REF_SCALE = 2.5; // px/mm the line weights were tuned for on screen
  const deviceScale = mmToPx / PRINT_REF_SCALE; // render() thickens strokes by this
  const pw = Math.max(1, Math.round(sh.widthMm * mmToPx));
  const ph = Math.max(1, Math.round(sh.heightMm * mmToPx));
  const c = document.createElement('canvas'); c.width = pw; c.height = ph;
  const cx = c.getContext('2d'); if (!cx) return null;
  cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, pw, ph);
  const pvDevice: PaperView = { scale: mmToPx, ox: 0, oy: 0 };       // clip, in 300-dpi device px
  const pvRef: PaperView = { scale: PRINT_REF_SCALE, ox: 0, oy: 0 }; // model Viewport (render scales it by deviceScale)
  const opts: RenderOptions = { ...sheetRenderOpts(sh), deviceScale };
  // What print waits for, before it paints in one go.
  if (sh.schematic) await preloadSchematicImages(sh);
  await stenalLogoLoaded;
  const cart = cartiglioForSheet(sh);
  let logo = cart ? cartiglioLogo(cart.logoDataUri) : null;
  if (cart?.logoDataUri && !logo) logo = await loadImage(cart.logoDataUri).catch(() => null);
  paintSheet(cx, sh, { pv: pvDevice, modelPv: pvRef, opts, ink: '#111417', live: false, logo: () => logo });
  // The viewports draw transparent (the model composites over the page), so paint
  // white BEHIND everything to flatten the sheet — otherwise a printer that renders
  // alpha as black prints a mostly-black page.
  cx.save();
  cx.globalCompositeOperation = 'destination-over';
  cx.fillStyle = '#ffffff';
  cx.fillRect(0, 0, pw, ph);
  cx.restore();
  return c;
}

async function renderSheetToPng(sh: Sheet): Promise<string | null> {
  const c = await renderSheetToCanvas(sh);
  return c ? c.toDataURL('image/png') : null;
}

/** A small preview (PNG or JPEG, whichever is smaller) of a sheet — its longer
 *  side ~512 px — for the cloud gallery. Rendered directly at a low dpi, so it is
 *  the sheet exactly as it prints, just scaled down; '' if it can't render. */
export async function sheetPreviewDataUrl(sh: Sheet): Promise<string> {
  const dpi = (512 * 25.4) / Math.max(1, sh.widthMm, sh.heightMm);
  const c = await renderSheetToCanvas(sh, dpi);
  if (!c) return '';
  const png = c.toDataURL('image/png');
  const jpg = c.toDataURL('image/jpeg', 0.82);
  return jpg.length < png.length ? jpg : png;
}

export async function printSheet(sh: Sheet): Promise<void> {
  const png = await renderSheetToPng(sh);
  if (!png) return;
  const landscape = sh.orientation === 'landscape';
  // Pass the exact paper size so the sheet prints 1:1 with no margins (clipped,
  // never rescaled) and its orientation reaches the printer.
  await platform.printImage(png, landscape, exportName(sheetLabel(sh, curLang()) || doc.savedProjectName || 'StenalCAD'), sh.widthMm, sh.heightMm);
}

/** File ▸ Print All Sheets: render every sheet and print them as one multi-page
 *  job (one sheet per page, each at its own exact paper size / orientation). */
export async function printAllSheets(): Promise<void> {
  const sheets = state.drawing.sheets || [];
  if (!sheets.length) { flashStatus(tr('No sheets to print. Add a sheet (paper space) first.')); return; }
  flashStatus(tr('Preparing {n} sheet(s) for printing…').replace('{n}', String(sheets.length)));
  const pages: Array<{ dataUrl: string; widthMm: number; heightMm: number }> = [];
  for (const sh of sheets) {
    const png = await renderSheetToPng(sh);
    if (png) pages.push({ dataUrl: png, widthMm: sh.widthMm, heightMm: sh.heightMm });
  }
  if (!pages.length) { flashStatus(tr('Could not render any sheet for printing.')); return; }
  await platform.printImages(pages, exportName(doc.savedProjectName || 'StenalCAD'));
}

/** File ▸ Export ▸ PDF (single page): the current sheet as a one-page PDF sized
 *  to its exact paper format, no margins. Only meaningful in paper space. */
export async function exportSheetPdf(): Promise<void> {
  const sh = activeSheet();
  if (!sh) { flashStatus(tr('Open a sheet (paper space) to export it to PDF.')); return; }
  const png = await renderSheetToPng(sh);
  if (!png) return;
  const landscape = sh.orientation === 'landscape';
  const saved = await platform.exportSheetPdf(png, sh.widthMm, sh.heightMm, landscape, exportName(sheetLabel(sh, curLang()) || doc.savedProjectName || 'StenalCAD'));
  if (saved) flashStatus(tr('Exported to PDF.'));
}

/** File ▸ Export ▸ PDF (all pages): every sheet as one multi-page PDF, each page
 *  at its own exact paper size / orientation, no margins. */
export async function exportAllSheetsPdf(): Promise<void> {
  const sheets = state.drawing.sheets || [];
  if (!sheets.length) { flashStatus(tr('No sheets to export. Add a sheet (paper space) first.')); return; }
  flashStatus(tr('Rendering {n} sheet(s) for PDF…').replace('{n}', String(sheets.length)));
  const pages: Array<{ dataUrl: string; widthMm: number; heightMm: number }> = [];
  for (const sh of sheets) {
    const png = await renderSheetToPng(sh);
    if (png) pages.push({ dataUrl: png, widthMm: sh.widthMm, heightMm: sh.heightMm });
  }
  if (!pages.length) { flashStatus(tr('Could not render any sheet for PDF.')); return; }
  const saved = await platform.exportAllPdf(pages, exportName(doc.savedProjectName || 'StenalCAD'));
  if (saved) flashStatus(tr('Exported {n} sheets to PDF.').replace('{n}', String(pages.length)));
}
